import { MAX_NOTICE_PERIOD_DAYS_FAKE_OVERNIGHT } from '../constants';

export const isCallAlikeSubtype = (offer) =>
  offer.is_overnight && offer?.savings_product_name === 'call_alike';

export const renderRate = (value, intl, options) => {
  return intl.formatNumber(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  });
};

export const renderPercentage = (value, intl) => {
  return intl.formatNumber(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'percent',
  });
};

export const isEasyAccessAccount = (offer, isRegionAllowed) => {
  return isRegionAllowed && offer.is_overnight && !offer.is_notice_account;
};

// Notice Account Offers whose notice period are max 4 days are considered Overnight Offers.
export const isFakeOvernightOffer = ({
  is_overnight,
  is_notice_account,
  payout_period_after_termination,
}) =>
  is_overnight &&
  is_notice_account &&
  payout_period_after_termination <= MAX_NOTICE_PERIOD_DAYS_FAKE_OVERNIGHT;

export const isNoticeAccountOffer = (
  { is_overnight, is_notice_account, payout_period_after_termination },
  showSpecialNoticeAccountAsOvernight,
) => {
  if (showSpecialNoticeAccountAsOvernight) {
    return (
      is_overnight &&
      is_notice_account &&
      payout_period_after_termination > MAX_NOTICE_PERIOD_DAYS_FAKE_OVERNIGHT
    );
  }

  return is_overnight && is_notice_account;
};

const renderOvernightTerm = (offer, intl, config, renderInOfferOverview) => {
  if (isNoticeAccountOffer(offer, config.features?.showSpecialNoticeAccountAsOvernight)) {
    const daysAmount = offer.payout_period_after_termination;

    return `${daysAmount} ${intl.messages.terminationNoticePeriod}`;
  }

  if (offer.savings_product_name && renderInOfferOverview) {
    return intl.messages.offerOverviewCallalikeAvailability;
  }

  return intl.messages.overnightDeposit;
};

export const renderTerm = (offer, intl, config, renderInOfferOverview = false) => {
  if (offer.is_overnight && !isEasyAccessAccount(offer, config.showEasyAccessLabel)) {
    return renderOvernightTerm(offer, intl, config, renderInOfferOverview);
  }

  if (isEasyAccessAccount(offer, config.showEasyAccessLabel)) {
    return intl.messages.easyAccess;
  }

  if (offer.number_of_terms === 1) {
    return `1 ${intl.messages.month}`;
  }

  if (offer.number_of_terms === 12) {
    return `1 ${intl.messages.year}`;
  }

  if (
    (offer.number_of_terms > 1 && offer.number_of_terms < 12) ||
    (offer.number_of_terms > 12 && offer.number_of_terms % 6 !== 0)
  ) {
    return `${offer.number_of_terms} ${intl.messages.months}`;
  }

  return `${offer.number_of_terms / 12} ${intl.messages.years}`;
};

export const orderArrByIndex = (array, order, key) => {
  array.sort((a, b) => {
    if (order.indexOf(a[key]) > order.indexOf(b[key])) {
      return 1;
    }

    return -1;
  });

  return array;
};

export const localizeMoney = ({ value, intl, currency, options }) => {
  return intl.formatNumber(value, {
    style: 'currency',
    currency,
    ...options,
  });
};

const bookingInterval = {
  ANNUALLY: 1,
  QUARTERLY: 4,
  MONTHLY: 12,
};

export const calcInterestEarned = (offer, investmentAmount) => {
  let interestEarned = 0;

  if (offer.is_overnight) {
    interestEarned = (offer.effective_interest_rate / 100) * (365 / 360) * investmentAmount;
  } else if (!offer.is_in_foreign_currency) {
    if (offer.interest_compounding) {
      const interval = bookingInterval[offer.interest_booking_interval];

      interestEarned =
        investmentAmount *
          (1 + offer.nominal_interest_rate / 100 / interval) **
            ((interval * offer.number_of_terms) / 12) -
        investmentAmount;
    } else {
      interestEarned =
        (((offer.number_of_terms / 12) * offer.nominal_interest_rate) / 100) * investmentAmount;
    }
  }

  return interestEarned;
};

export const CHIPS_TYPES = {
  FOREIGN_CURRENCY: 'ForeignCurrency',
  SHARIA_ACCOUNT: 'ShariaAccount',
  FLEX_GELD: 'Flexgeld',
  FINANZ_TEST_BESTEN: 'FinanztestBestenlist',
  RATE_REDUCING_SOON: 'RateReducingSoon',
  RAISIN_EXCLUSIVE: 'Raisinexclusive',
  FULL_ONLINE: 'FullOnline',
  NO_WITHHOLDING_TAX: 'NoWithHoldingTax',
  SPECIAL: 'Special',
  FIXED_INVESTMENT_AMOUNT: 'FixedInvestmentAmount',
};

const DEPOSIT_GUARANTEE_LINKS = {
  GBR: 'https://fscs.org.uk/',
  SWE: 'https://riksgalden.se/en/',
  MLT:
    'https://mfsa.mt/consumers/consumer-awareness-and-education/banking/accounts/depositor-compensation-scheme/',
  GIB: 'https://gdgb.gi/',
};

export const getDepositGuaranteeLink = (countryCode) => DEPOSIT_GUARANTEE_LINKS[countryCode];

export const isNegativeInterestRate = (interestRate) => interestRate < 0;
